import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, Subject, takeUntil } from 'rxjs';

import { APIResponse } from 'src/app/core/models/api-response';
import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';
import { ButtonComponent } from 'src/app/ui/button/button.component';
import { HasPermissionDirective } from 'src/app/shared/has-permission.directive';
import { SharedModule } from 'src/app/shared/shared.module';
import { getQuotationStatusStyles } from 'src/app/shared/utils/quotation-status-styles';
import { QuotesService } from 'src/app/features/quotations/services/quotations.services';
import { IParseProduct, IQuotationDetail } from 'src/app/features/quotations/models/quotations.models';
import { deleteQuotationSuccess } from 'src/app/features/quotations/store/quotations.actions';
import { IOrderPrimaryAction, OrderPrimaryActionService, OrderSecondaryAction } from 'src/app/features/quotations/services/order-primary-action.service';
import { QuotationExportService } from 'src/app/features/quotations/services/quotation-export.service';
import { CopyableComponent } from 'src/app/ui/copyable/copyable.component';
import { CommonService } from 'src/app/shared/common.service';
import { IOrderLinePreview, ITimelineOrder } from '../../../models/timeline.models';
import {
  isMissingCustomerValue,
  orderCurrencyCode,
  orderCurrencySymbol,
} from '../../../utils/order-display.util';
import { ORDER_REFRESH_ACTIONS, refreshedOrderId } from '../../../utils/order-refresh.util';

/** Line-item rows rendered before the `+N more` link takes over (WB-7401). */
const MAX_PREVIEW_LINES = 3;

/** Skeleton row count of the in-progress state — matches the design. */
const SKELETON_ROWS = 3;

/**
 * The order artifact rendered inline in the Smart Inbox conversation timeline.
 *
 * Two states (WB-7401):
 *  - `creating` — a background order creation is running (WB-7402). Sparkle icon,
 *    animated title, skeleton table. Resolves when the thread is refetched.
 *  - `created`  — the full card: reference, last-modified, customer chip, the
 *    line-item table capped at `MAX_PREVIEW_LINES` rows, grand total, status badge, the same main
 *    action the order-detail page offers, and an overflow menu.
 *
 * Line items arrive inline with the thread payload as `order.lines_preview`
 * (WB-7444); a payload issued before it shipped carries none, and the card then
 * falls back to one `GET /v1/quotations/{id}` per order.
 */
@Component({
  selector: 'app-order-artifact',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatMenuModule,
    MatTooltipModule,
    // SharedModule exposes the `daysAgoOrLeft` pipe used by the header timestamp.
    SharedModule,
    SvgIconComponent,
    CopyableComponent,
    ButtonComponent,
    HasPermissionDirective,
  ],
  templateUrl: './order-artifact.component.html',
  styleUrls: ['./order-artifact.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderArtifactComponent implements OnDestroy {
  // ── Inputs / Outputs ────────────────────────────────────────────────────────

  @Input() state: 'creating' | 'created' = 'created';

  @Input()
  set order(value: ITimelineOrder | null) {
    this._order = value;
    if (value) this.loadLines(value);
  }
  get order(): ITimelineOrder | null {
    return this._order;
  }

  /** Open the order in the side-by-side panel (eye action, `+N more`, row click). */
  @Output() openOrder = new EventEmitter<ITimelineOrder>();
  /** Open the standalone order page in a new tab. */
  @Output() viewFullOrder = new EventEmitter<ITimelineOrder>();
  @Output() downloadPdf = new EventEmitter<ITimelineOrder>();
  /** A status transition completed — the parent should refetch the thread. */
  @Output() orderChanged = new EventEmitter<ITimelineOrder>();

  // ── Public properties ───────────────────────────────────────────────────────

  readonly skeletonRows = Array.from({ length: SKELETON_ROWS });
  readonly maxPreviewLines = MAX_PREVIEW_LINES;

  lines: IOrderLinePreview[] = [];
  linesLoading = false;
  linesLoadFailed = false;
  /** Count of every real line, so `+N more` is right even when only 3 are shown. */
  totalLines = 0;
  /** Status of record — the freshly fetched one when available, else the timeline's. */
  currentStatus = '';
  hidePricingColumns = false;
  allLinesNoPrice = false;
  /** Total of priced lines (WB-7024), preferred over the timeline's `total`. */
  pricedLinesTotal: number | null = null;
  /**
   * The associated company and the delivery location, from the quotation detail.
   * `undefined` until it resolves, `null` once it says there is none — see
   * `isKnownMissing` for why the two must not be conflated.
   */
  detailCompanyName: string | null | undefined = undefined;
  detailLocationName: string | null | undefined = undefined;

  /**
   * Rows still waiting to be matched. Held separately from `lines`, which is
   * capped at 5: on a six-unmatched-row order the cap would understate it.
   */
  unmatchedLinesCount = 0;

  // ── Private properties ──────────────────────────────────────────────────────

  private _order: ITimelineOrder | null = null;
  private loadedOrderId: number | null = null;
  private destroy$ = new Subject<void>();

  // ── Constructor ─────────────────────────────────────────────────────────────

  constructor(
    private quotesService: QuotesService,
    private primaryActionService: OrderPrimaryActionService,
    private exportService: QuotationExportService,
    private commonService: CommonService,
    private translate: TranslateService,
    private actions$: Actions,
    private cdr: ChangeDetectorRef
  ) {
    this.actions$
      .pipe(
        ofType(...ORDER_REFRESH_ACTIONS),
        filter((action) => !!this._order && refreshedOrderId(action) === this._order.order.id),
        takeUntil(this.destroy$)
      )
      .subscribe((action) => {
        if (!this._order) return;
        // A deleted order has nothing left to read: refetching its lines returns
        // 404 and the interceptor stacks an error toast on top of the
        // "Order deleted successfully" one. The thread refetch below drops the
        // card altogether.
        if (action.type !== deleteQuotationSuccess.type) {
          this.loadedOrderId = null;
          this.loadLines(this._order);
        }
        this.orderChanged.emit(this._order);
        // OnPush: nothing marked this view. The inline branch of `loadLines`
        // mutates the fields and returns synchronously, so without this the card
        // repaints only because the parent's refetch happens to rewrite `order`.
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Display helpers ─────────────────────────────────────────────────────────

  get currencySymbol(): string {
    return orderCurrencySymbol(this._order?.order);
  }

  /** ISO code prefixing the grand total, e.g. `EUR 336,94` in the design. */
  get currencyCode(): string {
    return orderCurrencyCode(this._order?.order);
  }

  /**
   * The associated **company**, not the location.
   *
   * Prefers `location.company_name` off the quotation detail — the exact field the
   * order-detail page and the side-by-side panel display — and falls back to the
   * thread payload's `customer_name` until that detail resolves.
   */
  get companyName(): string {
    const fromDetail = this.detailCompanyName?.trim();
    if (fromDetail && !isMissingCustomerValue(fromDetail)) return fromDetail;
    return this._order?.order.customer_name?.trim() ?? '';
  }

  /**
   * True when the order has no customer association. The backend sends `N/A` for an
   * unassociated customer rather than omitting the field, so treat that as missing
   * too — an order without a customer cannot be completed either way.
   */
  get isCustomerMissing(): boolean {
    return isMissingCustomerValue(this.companyName);
  }

  /** WB-7443. The fallback covers payloads issued before the field shipped. */
  get lastModifiedAt(): string | undefined {
    return this._order?.order.last_modified ?? this._order?.order.created_at;
  }

  get total(): number {
    return this.pricedLinesTotal ?? this._order?.order.total ?? 0;
  }

  /** Lines hidden behind the `+N more item(s)` link. */
  get hiddenLinesCount(): number {
    return Math.max(0, this.totalLines - this.lines.length);
  }

  /**
   * WB-7569 — the order total obeys the same hide-pricing snapshot as the Price
   * and Total columns, so a tenant that hides pricing data sees no monetary
   * value anywhere on the card.
   */
  get showOrderTotal(): boolean {
    return !this.hidePricingColumns;
  }

  /**
   * The summary strip carries only the `+N more` link and the total. With
   * pricing hidden and every line already listed it has nothing left to show,
   * and its 12px padding plus top border would leave an empty banded row —
   * so drop the row entirely rather than rendering it blank.
   */
  get showSummaryRow(): boolean {
    return this.showOrderTotal || this.hiddenLinesCount > 0;
  }

  getStatusStyles(status: string): { [key: string]: string } {
    return getQuotationStatusStyles(status);
  }

  /**
   * Quantity as the order detail renders it: in the selected handling unit when the
   * line has one, otherwise the base quantity with a units suffix.
   */
  getQuantityLabel(line: IOrderLinePreview): string {
    if (line.quantity_handling_unit != null && line.handling_unit_name) {
      return `${line.quantity_handling_unit}`;
    }
    return `${line.quantity}`;
  }

  getQuantityUnit(line: IOrderLinePreview): string | null {
    if (line.quantity_handling_unit != null && line.handling_unit_name) {
      return line.handling_unit_name;
    }
    return null;
  }

  // ── Actions ─────────────────────────────────────────────────────────────────

  /**
   * The order still needs a human before it can be approved: rows the AI could
   * not match, no company, or no delivery location. Read off the payload so the
   * inline path does not have to make the request the preview exists to avoid.
   */
  get needsReview(): boolean {
    if (!this._order) return false;
    return (
      this.unmatchedLinesCount > 0 ||
      this.isKnownMissing(this.associatedCompanyName) ||
      this.isKnownMissing(this.locationName)
    );
  }

  /**
   * The order's own company — `company_name` inline, the detail's once the
   * fallback resolves. Not `companyName` above, a display string falling back to
   * `customer_name`, which collapses to the location alone when there is no
   * company and so reads "complete" on exactly the orders this must catch.
   */
  get associatedCompanyName(): string | null | undefined {
    const inline = this._order?.order.company_name;
    return inline !== undefined ? inline : this.detailCompanyName;
  }

  /** `location_name` inline (WB-7444), or the detail's once the fallback resolves. */
  get locationName(): string | null | undefined {
    const inline = this._order?.order.location_name;
    return inline !== undefined ? inline : this.detailLocationName;
  }

  /**
   * Missing, and we have been told so. `undefined` means nobody has said yet —
   * a payload predating the field, or a detail in flight. Treating that as
   * absent made every card offer Review on a complete order.
   */
  private isKnownMissing(value: string | null | undefined): boolean {
    return value !== undefined && isMissingCustomerValue(value);
  }

  /**
   * Null while the order needs review — the card offers Review instead, a
   * replacement rather than a disabled state: the next step really is the
   * side-by-side, and greying the button out would leave the wrong one on screen.
   */
  get primaryAction(): IOrderPrimaryAction | null {
    if (this.needsReview) return null;
    return this.primaryActionService.getPrimaryAction(this.currentStatus);
  }

  /** Review resolves in the side-by-side, the only surface that can match a row. */
  onReview(event: MouseEvent): void {
    event.stopPropagation();
    this.onOpenOrder();
  }

  onPrimaryAction(event: MouseEvent): void {
    event.stopPropagation();
    const action = this.primaryAction;
    if (!action || !this._order) return;
    this.primaryActionService.confirm(action, {
      id: this._order.order.id,
      status: this.currentStatus,
    });
  }

  onOpenOrder(event?: MouseEvent): void {
    event?.stopPropagation();
    if (this._order) this.openOrder.emit(this._order);
  }

  onViewFullOrder(event: MouseEvent): void {
    event.stopPropagation();
    if (this._order) this.viewFullOrder.emit(this._order);
  }

  onDownloadPdf(event: MouseEvent): void {
    event.stopPropagation();
    if (this._order) this.downloadPdf.emit(this._order);
  }

  /** Cancel / duplicate / delete, behind the order page's own confirmation dialog. */
  onSecondaryAction(action: OrderSecondaryAction, event: MouseEvent): void {
    event.stopPropagation();
    if (!this._order) return;
    this.primaryActionService.confirmSecondary(action, {
      id: this._order.order.id,
      name: this._order.order.name,
    });
  }

  get canCancel(): boolean {
    return this.primaryActionService.canCancel(this.currentStatus);
  }

  get canDelete(): boolean {
    return this.primaryActionService.canDelete(this.currentStatus);
  }

  /**
   * Export the order's lines to CSV through the shared `QuotationExportService`,
   * the same path the order-detail page uses, so the output stays identical.
   *
   * The card only holds the 3-line preview, so fetch the full line set first —
   * otherwise the export would silently truncate a large order.
   */
  onDownloadCsv(event: MouseEvent): void {
    event.stopPropagation();
    const order = this._order?.order;
    if (!order) return;

    this.quotesService
      .getQuotationById(order.id, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: APIResponse<IQuotationDetail>) => {
          const products = response.object?.products ?? [];
          if (!products.length) {
            this.commonService.showToast(
              this.translate.instant('quotations_and_orders.csv_no_products'),
              'INFO'
            );
            return;
          }
          this.exportService.downloadProductsCsv(products, `${order.name}.csv`);
        },
        error: () => {
          this.commonService.showToast(
            this.translate.instant('smart_inbox.order_artifact.csv_export_failed'),
            'ERROR'
          );
        },
      });
  }

  // ── Private methods ─────────────────────────────────────────────────────────

  /**
   * Resolve the line-item table. Prefers the inline `lines_preview` (WB-7444) and
   * falls back to the quotation detail, which is also the only source of the
   * pricing flags and of the authoritative status.
   */
  private loadLines(timelineOrder: ITimelineOrder): void {
    const { order } = timelineOrder;
    this.currentStatus = order.state;

    if (order.lines_preview?.length) {
      this.lines = order.lines_preview.slice(0, MAX_PREVIEW_LINES);
      this.totalLines = order.total_order_lines ?? order.lines_preview.length;
      this.hidePricingColumns = !!order.hide_pricing_columns;
      this.allLinesNoPrice = !!order.all_lines_no_price;
      // WB-7024: same field the detail prints. Left null on a payload that does
      // not carry it, so `total` falls back to the order total as before rather
      // than to a zero.
      this.pricedLinesTotal = order.priced_lines_total ?? null;
      this.unmatchedLinesCount = order.unmatched_lines_count ?? 0;
      this.loadedOrderId = order.id;
      return;
    }

    if (this.loadedOrderId === order.id) return;
    this.loadedOrderId = order.id;
    this.linesLoading = true;
    this.linesLoadFailed = false;

    this.quotesService
      .getQuotationById(order.id, true, 1, MAX_PREVIEW_LINES, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: APIResponse<IQuotationDetail>) => {
          this.linesLoading = false;
          if (response.object) this.applyQuotationDetail(response.object);
          this.cdr.markForCheck();
        },
        error: () => {
          this.linesLoading = false;
          this.linesLoadFailed = true;
          // Let a later render retry rather than caching the failure forever.
          this.loadedOrderId = null;
          this.cdr.markForCheck();
        },
      });
  }

  private applyQuotationDetail(detail: IQuotationDetail): void {
    this.lines = (detail.products ?? []).slice(0, MAX_PREVIEW_LINES).map(mapQuotationLineToPreview);
    this.totalLines =
      detail.total_order_lines ?? detail.lines_metadata?.count ?? this.lines.length;
    // The unmatched rows cannot come from here: `loadLines` asks for this detail
    // with `include_ai_details=false` (it is the expensive half of the payload on
    // a large AI quote), so `ai_details` is always null on this path. They arrive
    // inline instead, with `unmatched_lines_count`. Reset rather than left as is,
    // so a count read off a previous payload never survives into this one.
    this.unmatchedLinesCount = 0;
    this.hidePricingColumns = !!detail.hide_pricing_columns;
    this.allLinesNoPrice = !!detail.all_lines_no_price;
    this.pricedLinesTotal = detail.priced_lines_total ?? detail.amount_total ?? null;
    this.detailCompanyName = detail.location?.company_name ?? null;
    this.detailLocationName = detail.location?.name ?? null;
    if (detail.status) this.currentStatus = detail.status;
  }
}

/**
 * Flatten an order line into the artifact's preview shape — the same shape the
 * thread payload serves inline (WB-7444), so this is only needed on the
 * legacy-payload fallback path.
 */
export function mapQuotationLineToPreview(line: IParseProduct): IOrderLinePreview {
  const selectedHandlingUnit = (line.handling_units ?? []).find((hu) => hu.selected) ?? null;
  return {
    id: line.order_line_id ?? line.id,
    product_name: line.name,
    sku: line.sku ?? '',
    quantity: line.quantity,
    quantity_handling_unit: line.quantity_handling_unit ?? null,
    handling_unit_name: selectedHandlingUnit?.name ?? null,
    final_price: line.final_price ?? line.price_unit,
    total: line.total,
  };
}
